import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { OrderHubAttachmentsTab } from '@/app/(client)/orders-hub/-components/OrderHubAttachmentsTab';
import { OrderHubUploadDocumentsDialog } from '@/app/(client)/orders-hub/-components/OrderHubUploadDocumentsDialog';
import {
  orderHubAttachmentsQueryOptions,
  useOrderHubAttachments,
} from '@/app/(client)/orders-hub/-hooks/useOrderHubAttachments';
import { useOrderHubOrderDetail } from '@/app/(client)/orders-hub/-hooks/useOrderHubOrderDetail';
import { useOrderHubOrderLinesList } from '@/app/(client)/orders-hub/-hooks/useOrderHubOrderLinesList';
import { useOrderHubUploadDocuments } from '@/app/(client)/orders-hub/-hooks/useOrderHubUploadDocuments';
import { useFeatures } from '@/hooks/useFeatures';
import { useTable } from '@/hooks/useTable';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';

export const Route = createFileRoute(
  '/(client)/orders-hub/(pages)/(detail)/$id/attachments',
)({
  loader: ({ params }) =>
    queryClient.ensureQueryData(
      orderHubAttachmentsQueryOptions(params.id, { page: 1, count: 20 }),
    ),
  component: OrderHubAttachmentsRoute,
});

function OrderHubAttachmentsRoute() {
  const { id } = Route.useParams();
  const table = useTable('orders-hub-attachments-v1', {
    mode: 'state',
    initialState: { limit: 20 },
  });
  const { data, isLoading } = useOrderHubAttachments(id, {
    page: table.page,
    count: table.limit,
  });
  const { getTranslation: t } = useTranslator({ page: 'ordersHub' });
  const { isCascadeTracingEnabled } = useFeatures();
  const { data: order } = useOrderHubOrderDetail(id);
  const { data: lines = [] } = useOrderHubOrderLinesList(id);
  const upload = useOrderHubUploadDocuments(id);

  const lineNumbersById = useMemo(() => {
    const map = new Map<string, string>();
    for (const line of lines) {
      map.set(line.id, line.orderLineNumber ?? String(line.lineNo ?? line.id));
    }
    return map;
  }, [lines]);

  const canUpload =
    isCascadeTracingEnabled &&
    !order?.isArchived &&
    (order?.permissions?.includes('edit') ?? false);

  return (
    <>
      <OrderHubUploadDocumentsDialog upload={upload} t={t} />
      <OrderHubAttachmentsTab
        rows={data?.data ?? []}
        totalCount={data?.meta.totalCount ?? 0}
        isLoading={isLoading}
        page={table.page}
        count={table.limit}
        onPaginationChange={table.onPaginationChange}
        lineNumbersById={lineNumbersById}
        canUpload={canUpload}
        onUpload={upload.openDialog}
      />
    </>
  );
}
